import React, { useEffect, useRef, useState } from 'react';
import { AlarmClock, Bell, ListTodo, Lock, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePerfil } from '../context/PerfilContext';
import { useBloqueo } from '../context/BloqueoContext';
import { useTareas } from '../context/TareasContext';
import { useTheme } from '../context/ThemeContext';
import { esApp } from '../utils/plataforma';
import { guardarVibraciones, vibracionesActivas } from '../utils/hapticos';
import { estadoNotificaciones, activarNotificaciones } from '../utils/notificaciones';
import { leerAjustes, guardarAjustes } from '../utils/recordatoriosGastos';
import { leerAjustesTareas, guardarAjustesTareas, reprogramarAvisosTareas } from '../utils/avisosTareas';
import { activarRecordatorios, esIOS, recordatoriosActivos, sincronizarRecordatorios } from '../utils/recordatoriosTareas';
import {
  AjusteDispositivo, AJUSTES_DISPOSITIVO, PreferenciasCuenta as Prefs, guardarEnCuenta, leerRespondidas,
  marcarRespondidas, queOfrecer, queSembrar,
} from '../utils/preferencias';
import { ModalBase, botonPrincipal, botonSecundario } from './tareas/ui';

/**
 * Las preferencias que siguen a la cuenta (ver src/utils/preferencias.ts):
 * - Tema y vibraciones: lo de la cuenta se aplica aquí; si la cuenta aún no
 *   lo tiene, se sube lo de este dispositivo.
 * - Face ID, notificaciones y avisos: si en la cuenta están encendidos y aquí
 *   no (app recién instalada, iPhone nuevo), se pregunta si activarlos.
 */
export const PreferenciasCuenta: React.FC = () => {
  const { user } = useAuth();
  const { perfil, cargandoPerfil, tieneModulo } = usePerfil();
  const bloqueo = useBloqueo();
  const { tareas, casas, conPlusTareas, marcarHecha, cargando: cargandoTareas } = useTareas();
  const { tema, setTema } = useTheme();
  const [ofrecer, setOfrecer] = useState<AjusteDispositivo[]>([]);
  // Se pregunta una vez por sesión: lo que se active desde la pregunta no la reabre
  const preguntadoRef = useRef(false);

  const prefs: Prefs = perfil?.preferencias ?? {};
  const huella = JSON.stringify(prefs);

  // ── Tema y vibraciones ───────────────────────────────────────────────────
  useEffect(() => {
    if (!perfil) return;
    if (prefs.tema) { if (prefs.tema !== tema) setTema(prefs.tema, false); }
    else if (tema !== 'sistema') guardarEnCuenta({ tema });

    if (prefs.vibraciones !== undefined) {
      if (prefs.vibraciones !== vibracionesActivas()) guardarVibraciones(prefs.vibraciones);
    } else if (esApp && !vibracionesActivas()) guardarEnCuenta({ vibraciones: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huella, !!perfil]);

  // ── Ajustes del dispositivo ──────────────────────────────────────────────
  const conFinanzas = tieneModulo('finanzas');
  const conTareas = tieneModulo('tareas');
  useEffect(() => {
    if (preguntadoRef.current || !esApp || !user || !perfil || cargandoPerfil || !bloqueo.listo || (conTareas && cargandoTareas)) return;
    let vivo = true;
    (async () => {
      // null: aquí no se puede usar
      const [push, gastos, avisos, recordatorios] = await Promise.all([
        estadoNotificaciones(), leerAjustes(), leerAjustesTareas(), recordatoriosActivos(),
      ]);
      const aqui: Record<AjusteDispositivo, boolean | null> = {
        bloqueo: bloqueo.disponible ? bloqueo.activo : null,
        notificaciones: push === 'no-disponible' ? null : push === 'activadas',
        recordatoriosGastos: conFinanzas ? gastos.inactividad || gastos.diario || gastos.resumenMes : null,
        avisosTareas: conTareas ? avisos.activos : null,
        recordatoriosTareas: conTareas && esIOS && conPlusTareas ? recordatorios : null,
      };
      // Lo que ya estaba encendido aquí antes de guardarse en la cuenta
      const sembrar = queSembrar(prefs, aqui);
      if (sembrar.length) {
        guardarEnCuenta({
          ...(sembrar.includes('bloqueo') && { bloqueo: true }),
          ...(sembrar.includes('notificaciones') && { notificaciones: true }),
          ...(sembrar.includes('recordatoriosGastos') && { recordatoriosGastos: gastos }),
          ...(sembrar.includes('avisosTareas') && { avisosTareas: avisos }),
          ...(sembrar.includes('recordatoriosTareas') && { recordatoriosTareas: true }),
        });
      }
      const lista = queOfrecer(prefs, aqui, await leerRespondidas(user.uid));
      if (vivo && lista.length && !preguntadoRef.current) {
        preguntadoRef.current = true;
        setOfrecer(lista);
      }
    })().catch(e => console.warn('Preferencias del dispositivo', e));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, huella, !!perfil, cargandoPerfil, bloqueo.listo, bloqueo.activo, cargandoTareas, conFinanzas, conTareas, conPlusTareas]);

  if (!ofrecer.length || !user) return null;

  const activar = async (clave: AjusteDispositivo): Promise<string | null> => {
    switch (clave) {
      case 'bloqueo':
        try { await bloqueo.activar(); return null; }
        catch { return `No se ha podido comprobar ${bloqueo.nombreBiometria}.`; }
      case 'notificaciones':
        return (await activarNotificaciones(user.uid)) === 'activadas' ? null
          : 'Las notificaciones están bloqueadas. Actívalas en Ajustes de iOS → Notificaciones → myarinn.';
      case 'recordatoriosGastos':
        return prefs.recordatoriosGastos && await guardarAjustes(prefs.recordatoriosGastos) ? null
          : 'Los avisos para apuntar gastos necesitan las notificaciones de iOS.';
      case 'avisosTareas':
        if (!prefs.avisosTareas || !(await guardarAjustesTareas(prefs.avisosTareas))) {
          return 'Los avisos de tareas necesitan las notificaciones de iOS.';
        }
        await reprogramarAvisosTareas(tareas, casas, user.uid).catch(() => {});
        return null;
      case 'recordatoriosTareas':
        if (await activarRecordatorios() === 'denegado') {
          return 'El iPhone no deja usar Recordatorios. Actívalo en Ajustes → myarinn → Recordatorios.';
        }
        await sincronizarRecordatorios({ tareas, casas, uid: user.uid, conPlus: conPlusTareas, marcarHecha })
          .catch(() => {});
        return null;
    }
  };

  return <ModalRestaurar ofrecer={ofrecer} prefs={prefs} nombreBiometria={bloqueo.nombreBiometria}
    activar={activar}
    onClose={() => { marcarRespondidas(user.uid, ofrecer).catch(() => {}); setOfrecer([]); }} />;
};

const TEXTOS: Record<AjusteDispositivo, { icono: React.ReactNode; titulo: (bio: string) => string; detalle: (p: Prefs) => string }> = {
  bloqueo: {
    icono: <Lock className="w-5 h-5" />,
    titulo: bio => `Bloquear con ${bio}`,
    detalle: () => 'Pedirlo al abrir la app.',
  },
  notificaciones: {
    icono: <Bell className="w-5 h-5" />,
    titulo: () => 'Notificaciones',
    detalle: () => 'Recordatorios de A medias, préstamos y tareas que te asignan.',
  },
  recordatoriosGastos: {
    icono: <AlarmClock className="w-5 h-5" />,
    titulo: () => 'Avisos para apuntar gastos',
    detalle: p => {
      const r = p.recordatoriosGastos;
      return [
        r?.diario && `cada día a las ${r.hora}`,
        r?.inactividad && `tras ${r.dias} días sin entrar`,
        r?.resumenMes && 'resumen del mes',
      ].filter(Boolean).join(', ').replace(/^./, c => c.toUpperCase()) + '.';
    },
  },
  avisosTareas: {
    icono: <Smartphone className="w-5 h-5" />,
    titulo: () => 'Avisos de tareas',
    detalle: p => `Las que te tocan; las que no tienen hora, a las ${p.avisosTareas?.horaResumen ?? '09:00'}.`,
  },
  recordatoriosTareas: {
    icono: <ListTodo className="w-5 h-5" />,
    titulo: () => 'Tareas en Recordatorios',
    detalle: () => 'Las tareas que te tocan, en la app Recordatorios del iPhone.',
  },
};

const ModalRestaurar: React.FC<{
  ofrecer: AjusteDispositivo[];
  prefs: Prefs;
  nombreBiometria: string;
  activar: (c: AjusteDispositivo) => Promise<string | null>;
  onClose: () => void;
}> = ({ ofrecer, prefs, nombreBiometria, activar, onClose }) => {
  const [marcadas, setMarcadas] = useState<Set<AjusteDispositivo>>(() => new Set(ofrecer));
  const [ocupado, setOcupado] = useState(false);
  const [fallos, setFallos] = useState<string[] | null>(null);

  const cambiar = (c: AjusteDispositivo) => setMarcadas(m => {
    const n = new Set(m);
    if (n.has(c)) n.delete(c); else n.add(c);
    return n;
  });

  const aceptar = async () => {
    setOcupado(true);
    const errores: string[] = [];
    // Una a una: cada permiso de iOS sale en su propio aviso
    for (const c of AJUSTES_DISPOSITIVO.filter(c => marcadas.has(c))) {
      const error = await activar(c).catch(() => 'No se ha podido activar.');
      if (error && !errores.includes(error)) errores.push(error);
    }
    setOcupado(false);
    if (errores.length) setFallos(errores);
    else onClose();
  };

  return (
    <ModalBase titulo="¿Lo activamos aquí también?" onClose={onClose}>
      <div className="p-5 space-y-4">
        {fallos ? (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">Casi todo listo, pero algo no se ha podido activar:</p>
            <ul className="space-y-2">
              {fallos.map(f => <li key={f} className="text-sm text-amber-700 dark:text-amber-400">{f}</li>)}
            </ul>
            <p className="text-xs text-gray-500 dark:text-gray-400">Puedes volver a intentarlo en Configuración → Perfil.</p>
            <button onClick={onClose} className={`w-full py-2.5 ${botonPrincipal}`}>Entendido</button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Lo tenías activado en tu cuenta, pero en este dispositivo no lo está (por ejemplo, porque acabas de
              instalar la app). Necesita tu permiso, así que te lo preguntamos.
            </p>
            <div className="space-y-2">
              {ofrecer.map(c => (
                <label key={c} className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 cursor-pointer">
                  <span className="text-gray-500 dark:text-gray-400 mt-0.5">{TEXTOS[c].icono}</span>
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-white">{TEXTOS[c].titulo(nombreBiometria)}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{TEXTOS[c].detalle(prefs)}</span>
                  </span>
                  <input type="checkbox" checked={marcadas.has(c)} onChange={() => cambiar(c)}
                    className="w-5 h-5 mt-0.5 accent-lumbre-600" />
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} disabled={ocupado} className={`flex-1 py-2.5 ${botonSecundario}`}>Ahora no</button>
              <button onClick={aceptar} disabled={ocupado || !marcadas.size} className={`flex-1 py-2.5 ${botonPrincipal}`}>
                {ocupado ? 'Activando…' : 'Activar'}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalBase>
  );
};
